import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { RefreshCw, Loader2, Trash2, RotateCcw } from 'lucide-react';

export default function DeletedRecords() {
  const [deletedRecords, setDeletedRecords] = useState([]);
  const [deletedRecordsLoading, setDeletedRecordsLoading] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState('leave_requests');
  const [availableCollections, setAvailableCollections] = useState([]);

  const fetchDeletedRecords = async () => {
    try {
      setDeletedRecordsLoading(true);
      const response = await api.get(`/admin/deleted-records?collection=${selectedCollection}&limit=50`);
      if (response.data.success) {
        setDeletedRecords(response.data.data.records || []);
        setAvailableCollections(response.data.data.collections || []);
      }
    } catch (error) {
      console.error('Error fetching deleted records:', error);
      toast.error('Silinen kayitlar yuklenemedi');
    } finally {
      setDeletedRecordsLoading(false);
    }
  };

  const handleRestoreRecord = async (recordId) => {
    try {
      const response = await api.post(`/admin/deleted-records/${recordId}/restore`);
      if (response.data.success) { toast.success('Kayit basariyla geri yuklendi'); fetchDeletedRecords(); }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Geri yukleme hatasi');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDeletedRecords(); }, [selectedCollection]);

  return (
    <Card data-testid="deleted-records-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Trash2 className="h-5 w-5 text-red-500" />Silinen Kayitlar Arsivi</CardTitle>
        <CardDescription>Silinen izin talepleri ve diger kayitlari goruntuleyin ve geri yukleyin</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium">Tablo:</span>
          <Select value={selectedCollection} onValueChange={setSelectedCollection}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="leave_requests">Izin Talepleri</SelectItem>
              <SelectItem value="users">Kullanicilar</SelectItem>
              <SelectItem value="documents">Belgeler</SelectItem>
              {availableCollections.filter(c => !['leave_requests', 'users', 'documents'].includes(c)).map(col => (
                <SelectItem key={col} value={col}>{col}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchDeletedRecords} disabled={deletedRecordsLoading} data-testid="refresh-deleted-records-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${deletedRecordsLoading ? 'animate-spin' : ''}`} />Yenile
          </Button>
        </div>

        {deletedRecordsLoading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : deletedRecords.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Trash2 className="h-10 w-10 mx-auto mb-2 text-slate-300" />
            <p>Bu tabloda silinen kayit bulunamadi</p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Silinen Kayit</TableHead>
                  <TableHead>Silen Kisi</TableHead>
                  <TableHead>Silinme Tarihi</TableHead>
                  <TableHead>Sebep</TableHead>
                  <TableHead className="w-24">Islem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deletedRecords.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>
                      <div className="font-medium">
                        {selectedCollection === 'leave_requests' ? (
                          <>
                            {record.record_data?.leave_type || 'Izin'} - {record.record_data?.days_count || '?'} gun
                            <div className="text-xs text-slate-500">{record.record_data?.start_date} &rarr; {record.record_data?.end_date}</div>
                          </>
                        ) : (
                          <span>{record.original_id}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{record.deleted_by_name || '-'}</TableCell>
                    <TableCell>{record.deleted_at ? new Date(record.deleted_at).toLocaleString('tr-TR') : '-'}</TableCell>
                    <TableCell className="text-sm text-slate-500 max-w-xs truncate">{record.reason || '-'}</TableCell>
                    <TableCell>
                      {record.can_restore ? (
                        <Button size="sm" variant="outline" onClick={() => handleRestoreRecord(record.id)} className="text-green-600 hover:text-green-700" data-testid={`restore-record-${record.id}`}>
                          <RotateCcw className="h-3 w-3 mr-1" />Geri Yukle
                        </Button>
                      ) : (
                        <Badge variant="secondary">Geri Yuklendi</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
